import {Component, EventEmitter, Inject, OnDestroy, OnInit, Output} from '@angular/core';
import {FirebaseService} from "../../services/firebase.service";
import {MatListModule} from "@angular/material/list";
import {MatButtonModule} from "@angular/material/button";
import {AsyncPipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {HttpClientModule} from "@angular/common/http";
import {IAuthor, IAuthorInList} from "../../common/interfaces";
import {ActivatedRoute, Router} from "@angular/router";
import {BehaviorSubject, filter, Subscription} from "rxjs";
import {getLongAuthorName} from "../../common/service-functions";
import {map} from "rxjs/operators";
import {WINDOW} from "../../window";
import {HostService} from "../../services/host.service";


@Component({
  selector: 'app-authors-list',
  standalone: true,
  providers: [FirebaseService],
  imports: [
    MatListModule, MatButtonModule, NgForOf, AsyncPipe, NgClass,
    MatProgressSpinnerModule, NgIf, HttpClientModule
  ],
  templateUrl: './authors-list.component.html',
  styleUrl: './authors-list.component.sass'
})

export class AuthorsListComponent implements OnDestroy, OnInit {
  protected authorsList: BehaviorSubject<IAuthorInList[]> = new BehaviorSubject<IAuthorInList[]>([]);
  @Output() author_emitter: EventEmitter<IAuthorInList> = new EventEmitter<IAuthorInList>();
  author_id: string = '';
  showSpinner: boolean = false;
  private authorsSubscription: Subscription | undefined;
  private  pathSubscription: Subscription | undefined;
  private targetWindow: Window | null = null;
  private host: string = this.hostService.host;

  constructor(private route: ActivatedRoute, private firebaseService: FirebaseService,
              @Inject(WINDOW) private window: Window | null,
              private hostService: HostService, protected router: Router) {
    this.setEnvironmentRelatedParams();
    this.showSpinner = true;
  }

  ngOnInit() {
    this.authorsSubscription = this.firebaseService.authorsList.pipe(filter((v,i) => v.size > 0)).subscribe((authors) => {
      let authorsListl = this.prepareAuthorsList(authors);
      this.showSpinner = false;
      this.propogateNewAuthorsList(authorsListl);
      this.pathSubscription = this.route.url.pipe(
        map(segments => segments.map(segment => segment.path))
      ).subscribe(pathParts => {
        if (pathParts.length > 0) {
          this.updateListing(pathParts);
        }
      });
    });
  }

  /**
   * Updates and propagates a new list of authors filtered by their publications
   * and sorted by specific criteria.
   *
   * @param {IAuthorInList[]} authorsListl - Array of author objects where each
   * object represents an author and their associated details.
   * @return {void} Does not return a value.
   */
  private propogateNewAuthorsList(authorsListl: IAuthorInList[]) {
    this.authorsList.next(authorsListl.filter((a) => (a.publications > 0 || a.forceShowInList))
      .sort((a, b) => {
        if (a.publications > 0 && (b.publications === undefined || b.publications === 0)) {
          return -1;
        }
        if ((a.publications === undefined || a.publications === 0) && b.publications > 0) {
          return 1;
        }
        return a.name.localeCompare(b.name);
      }));
  }

  /**
   * Prepares a list of authors based on the provided map of author data.
   *
   * Alternatively return a stub author with id 'i' and name '' if no authors are present.
   * Each author in the list is formatted with the longest name, a slug name (for URL usage),
   * and the number of publications associated with that author.
   * @param {Map<string, IAuthor>} authors - A map where the key is a unique identifier for each author and the value is an object containing author data.
   * @return {IAuthorInList[]} A list of authors formatted with their details, including the longest name, slug name, and publication count.
   */
  private prepareAuthorsList(authors: Map<string, IAuthor>) {
    let authorsListl: IAuthorInList[] = [];
    if (authors.size > 0) {
      authors.forEach((value: IAuthor, key) => {
        let longestName = getLongAuthorName(value.names);
        if (longestName.length > 0) {
          authorsListl.push({
            id: key, name: longestName,
            slug: value.slug,
            publications: value.publications,
            forceShowInList: value.forceShowInList
          });
        }
      });
    } else {
      authorsListl.push({id: 'i', name: '', publications: 1});
    }
    return authorsListl;
  }

  private setEnvironmentRelatedParams() {
    if (this.host && /^https:\/\/[\w-]{0,25}\.?bashanfoundation\.\w{2,4}/.test(this.host) && window !== null) {
      this.targetWindow = window.parent;
    }
  }

  updateListing(pathData: (string| undefined)[]) {
    const currentAuthorsList = this.authorsList.value;
    // console.log('currentAuthorsList', currentAuthorsList);
    let authorId = '';
    let authorsData: IAuthorInList | undefined = undefined;
  // console.log('pathData', pathData);
    if (currentAuthorsList.length > 0 && pathData.length === 2) {
      if (pathData[0] === 'author' && pathData[1] !== undefined && pathData[1] !== '') {
        authorsData = currentAuthorsList.find((a: IAuthorInList) => a.slug === pathData[1]?.toLowerCase());
        // console.log('authorsData', authorsData);
        authorId = authorsData ? authorsData.id : '';
      } else if (pathData[0] === 'list' && pathData[1] !== undefined && pathData[1] !== '') {
        authorId = pathData[1];
        authorsData = currentAuthorsList.find((a: IAuthorInList) => a.id === authorId);
      }

      if (authorId !== '' && authorsData !== undefined) {
        this.author_id = authorId;
        // console.log('authorsData2', authorsData);
        this.router.navigate([`/author/${authorsData.slug}`]);
        this.author_emitter.emit(authorsData);
        this.pushToHistoryStack(authorsData);
      }
    }
  }

  /**
   * Pushes the given author's data to the browser's history stack.
   * Handles both the current window (standalone) and a parent window (if ran in IFrame).
   *
   * @param {IAuthorInList} authorsData - The data of the author to be added to the history stack.
   *                                      This includes properties such as `name` and `slug` used
   *                                      for updating the URL and state information.
   * @return {void} This method does not return a value.
   */
  private pushToHistoryStack(authorsData: IAuthorInList) {
    if (this.targetWindow) {
      const message = {
        setUrlObj: {
          authorData: {
            authorId: authorsData.id,
            authorName: authorsData.name,
            slug: authorsData.slug,
          },
          title: `Publications for ${authorsData.name}`,
          url: `/author/${authorsData.slug}`
        }
      };
      console.log('pushing state to top window', message);
      this.targetWindow.postMessage(message, this.host);
    }
  }

  ngOnDestroy(): void {
    if (this.authorsSubscription) {
      this.authorsSubscription.unsubscribe();
    }
    if (this.pathSubscription) {
      this.pathSubscription.unsubscribe();
    }
  }
}
